import { createSelector } from '@reduxjs/toolkit';

import { getAuthState } from 'store/auth/selectors';
import { RootState } from 'store/rootReducer';

export const getNewDialogsCount = (state: RootState) => {
  const { dialogs } = state.messenger;

  if (Array.isArray(dialogs.data) && dialogs.data.length === 0) {
    return state.common.newDialogsCount;
  }

  /**
   * Add +1 for every dialog with unread_count > 0
   */
  return dialogs.data.reduce((acc, dialog) => {
    if (dialog.unread_count > 0) {
      return acc + 1;
    }

    return acc;
  }, 0);
};

export const getIsCommonStateLoading = (state: RootState) =>
  state.common.loading;

export const getHasNotifications = (state: RootState) =>
  state.common.hasNotifications;

export const getHasMatch = (state: RootState) => state.common.popups.hasMatch;

export const getPopularUsers = (state: RootState) => state.common.popularUsers;

export const getRedirectFromRestrictedPublicPages = (state: RootState) =>
  state.common.redirectFromRestrictedPublicPages;

export const getPrevPageHref = (state: RootState) => state.common.prevPageHref;

export const getCurrentLangSelector = (state: RootState) => {
  return state.common.currentLang;
};

export const getSupportedLangsSelector = (state: RootState) =>
  state.common.supportedLangs;

export const isTranslationsEnabledSelector = (state: RootState) =>
  state.common.translations.isEnabled;

export const getTranslationsBuildVersionSelector = (state: RootState) =>
  state.common.translations.buildVersion;

export const getBoostEpireDateSelector = (state: RootState) =>
  state.common.boostExpireDate;

export const isLandingValidationSkipEnabledSelector = (state: RootState) =>
  state.common.isLandingValidationSkipEnabled;

export const isLandingAdditionalPriceEnabledSelector = (state: RootState) =>
  state.common.isLandingAdditionalPriceEnabled;

export const getCsrfTokenSelector = (state: RootState) =>
  state.common.csrfToken;

// FEATURE FLAGS
export const getFeatureFlagsSelector = (state: RootState) =>
  state.common.featureFlags;

export const isLimitTimeOfferEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isLimitTimeOfferEnabled
);

export const isAdsEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isAdsEnabled
);

export const isNativeAdsEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isNativeAdsEnabled
);

export const isCanShowRetentionPopupSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isCanShowRetentionPopup
);

export const isCanShowGiftCreditsPopupSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isCanShowGiftCreditsPopup
);

export const isPromoteVipPopupEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isPromoteVipPopupEnabled
);

export const isPaymentFormIntentEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isPaymentFormIntentEnabled
);

export const isComplianceStatementEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isComplianceStatementEnabled
);

export const isStaticPopularUsersPhotoEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isStaticPopularUsersPhotoEnabled
);

export const isCarouselPaywallEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isCarouselPaywallEnabled
);

export const isRetentionFunnelsEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isRetentionFunnelsEnabled
);

export const isFreeVipEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isFreeVipEnabled
);

export const isAdditionalPriceEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isAdditionalPriceEnabled
);

export const isUpgradeDarkThemeEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isUpgradeDarkThemeEnabled
);

export const isOrganicPartnerIdSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isOrganicPartnerId
);

export const isNeedAgeVerificationEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isNeedAgeVerificationEnabled
);

export const is3dPackageEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.is3dPackageEnabled
);

export const isErrorTranslateEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isErrorTranslateEnabled
);

export const isPaywallMonthPackageEnabledSelector = createSelector(
  [getFeatureFlagsSelector, getAuthState],
  (featureFlags, auth) =>
    featureFlags.isPaywallMonthPackageEnabled && !auth.user?.is_buyer
);

export const isCompliancePaywallEnabledSelector = createSelector(
  [getFeatureFlagsSelector, getAuthState],
  (featureFlags, auth) =>
    featureFlags.isPaywallMonthPackageEnabled &&
    !auth.user?.is_buyer &&
    featureFlags.isCompliancePaywallEnabled
);

export const isJoinFeeEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isJoinFeeEnabled
);

export const isCreditsPriceOptimizationEnabledSelector = createSelector(
  getFeatureFlagsSelector,
  (featureFlags) => featureFlags.isCreditsPriceOptimizationEnabled
);
